import React, { useState } from 'react'
import {Download, Printer, Save, Check, Info, TrendingUp, Clock, Leaf, Mail, X} from 'lucide-react'
import { Estimate, CalculatorState, CountryConfig } from './types'
import { fallbackMaterialRates } from './constants'
import MaintenanceComparison from './MaintenanceComparison'

interface EstimateResultsProps {
  estimate: Estimate
  state: CalculatorState
  currentCountry: CountryConfig
  isAuthenticated: boolean
  saveSuccess: boolean
  onGeneratePDF: () => void
  onPrint: () => void
  onSave: () => void
  onShareEmail: (email: string) => Promise<void>
}

export default function EstimateResults({
  estimate,
  state,
  currentCountry,
  isAuthenticated,
  saveSuccess,
  onGeneratePDF,
  onPrint,
  onSave,
  onShareEmail
}: EstimateResultsProps) {
  const [showEmailModal, setShowEmailModal] = useState(false)
  const [emailInput, setEmailInput] = useState('')
  const [emailSending, setEmailSending] = useState(false)
  const [emailSuccess, setEmailSuccess] = useState(false)
  const [emailError, setEmailError] = useState('')

  const handleEmailShare = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!emailInput.trim()) return
    
    setEmailSending(true)
    setEmailError('')
    
    try {
      await onShareEmail(emailInput)
      setEmailSuccess(true)
      setTimeout(() => {
        setShowEmailModal(false)
        setEmailInput('')
        setEmailSuccess(false)
      }, 2000)
    } catch (error: any) {
      setEmailError(error?.message || 'Failed to send email. The recipient must be a registered user.')
    } finally {
      setEmailSending(false)
    }
  }

  return (
    <>
      <div className="space-y-3 mt-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <button
            type="button"
            onClick={onGeneratePDF}
            className="bg-blue-800 hover:bg-blue-900 text-white font-bold py-4 px-6 rounded-lg transition-colors text-lg shadow-lg flex items-center justify-center gap-2"
            aria-label="Download estimate as PDF document"
          >
            <Download size={20} aria-hidden="true" />
            <span className="hidden md:inline">Download PDF</span>
            <span className="md:hidden">PDF</span>
          </button>
          <button
            type="button"
            onClick={onPrint}
            className="bg-gray-700 hover:bg-gray-800 text-white font-bold py-4 px-6 rounded-lg transition-colors text-lg shadow-lg flex items-center justify-center gap-2"
            aria-label="Print estimate"
          >
            <Printer size={20} aria-hidden="true" />
            <span className="hidden md:inline">Print</span>
            <span className="md:hidden">Print</span>
          </button>
          <button
            type="button"
            onClick={() => setShowEmailModal(true)}
            className="bg-green-600 hover:bg-green-700 text-white font-bold py-4 px-6 rounded-lg transition-colors text-lg shadow-lg flex items-center justify-center gap-2"
            aria-label="Share estimate via email"
          >
            <Mail size={20} aria-hidden="true" />
            <span className="hidden md:inline">Email</span>
            <span className="md:hidden">Email</span>
          </button>
          {isAuthenticated && (
            <button
              type="button"
              onClick={onSave}
              disabled={saveSuccess}
              className={`font-bold py-4 px-6 rounded-lg transition-colors text-lg shadow-lg flex items-center justify-center gap-2 ${
                saveSuccess
                  ? 'bg-green-600 text-white cursor-default'
                  : 'bg-orange-500 hover:bg-orange-600 text-white'
              }`}
              aria-label="Save estimate to your account"
            >
              {saveSuccess ? (
                <><Check size={20} aria-hidden="true" /><span className="hidden md:inline">Saved!</span><span className="md:hidden">✓</span></>
              ) : (
                <><Save size={20} aria-hidden="true" /><span className="hidden md:inline">Save</span><span className="md:hidden">Save</span></>
              )}
            </button>
          )}
        </div>
        {!isAuthenticated && (
          <p className="text-sm text-gray-600 text-center">
            Sign in to save your estimates for future reference
          </p>
        )}
      </div>

      {/* Email Share Modal */}
      {showEmailModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-2xl max-w-md w-full p-6 relative animate-fadeIn">
            <button
              onClick={() => {
                setShowEmailModal(false)
                setEmailError('')
                setEmailSuccess(false)
              }}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
              aria-label="Close modal"
            >
              <X size={24} />
            </button>
            
            <div className="flex items-center gap-3 mb-4">
              <div className="bg-green-100 p-3 rounded-full">
                <Mail size={24} className="text-green-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900">Share Estimate via Email</h3>
            </div>
            
            {emailSuccess ? (
              <div className="text-center py-8">
                <Check size={48} className="text-green-600 mx-auto mb-3" />
                <p className="text-lg font-semibold text-green-600">Email sent successfully!</p>
              </div>
            ) : (
              <form onSubmit={handleEmailShare}>
                <div className="mb-4">
                  <label htmlFor="recipient-email" className="block text-sm font-medium text-gray-700 mb-2">
                    Recipient Email Address
                  </label>
                  <input
                    id="recipient-email"
                    type="email"
                    value={emailInput}
                    onChange={(e) => setEmailInput(e.target.value)}
                    placeholder="Enter email address"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    required
                    disabled={emailSending}
                  />
                  {emailError && (
                    <p className="text-red-600 text-sm mt-2">{emailError}</p>
                  )}
                  <p className="text-xs text-gray-500 mt-2">
                    Note: Recipient must be a registered user on our platform
                  </p>
                </div>
                
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                  <p className="text-sm text-gray-700 mb-2"><strong>What will be sent:</strong></p>
                  <ul className="text-xs text-gray-600 space-y-1">
                    <li>• Complete PDF estimate with cost breakdown</li>
                    <li>• 30-year maintenance cost comparison chart</li>
                    <li>• Detailed project specifications</li>
                  </ul>
                </div>
                
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      setShowEmailModal(false)
                      setEmailError('')
                    }}
                    className="flex-1 px-4 py-3 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 transition-colors"
                    disabled={emailSending}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-4 py-3 bg-green-600 hover:bg-green-700 text-white font-bold rounded-lg transition-colors flex items-center justify-center gap-2"
                    disabled={emailSending}
                  >
                    {emailSending ? (
                      <span>Sending...</span>
                    ) : (
                      <>
                        <Mail size={18} />
                        <span>Send Email</span>
                      </>
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      <div className="mt-6 space-y-4 animate-fadeIn" role="region" aria-labelledby="estimate-results">
        <div className="p-6 bg-blue-50 border-2 border-blue-800 rounded-lg">
          <p id="estimate-results" className="text-sm text-gray-600 mb-2">Total Estimated Cost Range:</p>
          <p className="text-4xl font-bold text-blue-800" aria-label={`Estimate range from ${currentCountry.currencySymbol}${estimate.low.toLocaleString()} to ${currentCountry.currencySymbol}${estimate.high.toLocaleString()}`}>
            {currentCountry.currencySymbol}{estimate.low.toLocaleString('en-US', { maximumFractionDigits: 0 })} - {currentCountry.currencySymbol}{estimate.high.toLocaleString('en-US', { maximumFractionDigits: 0 })}
          </p>
        </div>

        <div className="bg-gray-50 border border-gray-300 rounded-lg p-6">
          <div className="flex items-center mb-4">
            <Info size={20} className="text-blue-800 mr-2" aria-hidden="true" />
            <h3 className="font-bold text-lg text-gray-900">Cost Breakdown</h3>
          </div>
          <dl className="space-y-2 text-sm text-gray-700">
            <div className="flex justify-between">
              <dt>Base Material & Installation:</dt>
              <dd className="font-semibold">Included</dd>
            </div>
            {state.removeOldRoof === 'yes' && estimate.breakdown.tearOff > 0 && (
              <div className="flex justify-between">
                <dt>Tear Off ({state.numLayers} layer{state.numLayers > 1 ? 's' : ''}):</dt>
                <dd className="font-semibold">+{currentCountry.currencySymbol}{estimate.breakdown.tearOff.toLocaleString()}</dd>
              </div>
            )}
            {state.roofPitch !== 'low' && (
              <div className="flex justify-between">
                <dt>Pitch Adjustment ({state.roofPitch}):</dt>
                <dd className="font-semibold">+{((estimate.breakdown.pitchMultiplier - 1) * 100).toFixed(0)}%</dd>
              </div>
            )}
            {state.numStories > 1 && (
              <div className="flex justify-between">
                <dt>Multi-Story Labor ({state.numStories} stories):</dt>
                <dd className="font-semibold">+{((estimate.breakdown.storiesMultiplier - 1) * 100).toFixed(0)}%</dd>
              </div>
            )}
            {state.hasChimneys > 0 && (
              <div className="flex justify-between">
                <dt>Chimney Flashing ({state.hasChimneys}):</dt>
                <dd className="font-semibold">+{currentCountry.currencySymbol}{estimate.breakdown.chimneyFlashing.toLocaleString()}</dd>
              </div>
            )}
            {state.hasSkylights > 0 && (
              <div className="flex justify-between">
                <dt>Skylight Flashing ({state.hasSkylights}):</dt>
                <dd className="font-semibold">+{currentCountry.currencySymbol}{estimate.breakdown.skylightFlashing.toLocaleString()}</dd>
              </div>
            )}
            {estimate.breakdown.complexityFee > 0 && (
              <div className="flex justify-between">
                <dt>Complexity Fee ({state.hasValleys} valleys):</dt>
                <dd className="font-semibold">+{currentCountry.currencySymbol}{estimate.breakdown.complexityFee.toLocaleString()}</dd>
              </div>
            )}
            {estimate.breakdown.urgencyFee > 0 && (
              <div className="flex justify-between">
                <dt>Urgency Premium ({state.urgency}):</dt>
                <dd className="font-semibold">+{currentCountry.currencySymbol}{estimate.breakdown.urgencyFee.toLocaleString()}</dd>
              </div>
            )}
          </dl>
        </div>

        <p className="text-xs text-gray-500 text-center">
          *Estimates based on {state.squareFootage} sq ft in {currentCountry.postalCodeLabel.toLowerCase()} {state.zipCode} ({currentCountry.label}). Actual costs vary by location, contractor rates, permits, and unforeseen conditions. Currency: {currentCountry.currencySymbol}. This calculator provides planning guidance only.
        </p>
      </div>

      {/* Material Comparison Chart */}
      <div className="mt-8 bg-white border border-gray-300 rounded-lg p-6 shadow-lg" role="region" aria-labelledby="material-comparison">
        <h3 id="material-comparison" className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
          <TrendingUp size={24} className="text-orange-500" aria-hidden="true" />
          Material Comparison Chart
        </h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="bg-gray-100">
                <th className="text-left p-3 font-bold text-gray-900 border-b-2 border-gray-300">Material</th>
                <th className="text-left p-3 font-bold text-gray-900 border-b-2 border-gray-300">Cost/sq ft</th>
                <th className="text-left p-3 font-bold text-gray-900 border-b-2 border-gray-300">Lifespan</th>
                <th className="text-left p-3 font-bold text-gray-900 border-b-2 border-gray-300">Best For</th>
                <th className="text-left p-3 font-bold text-gray-900 border-b-2 border-gray-300">Key Benefits</th>
              </tr>
            </thead>
            <tbody>
              {[
                { id: 'asphalt', name: 'Asphalt Shingles', lifespan: '15-30 years', bestFor: 'Budget-conscious homeowners', benefits: 'Most affordable, easy installation, variety of colors', eco: false },
                { id: 'metal', name: 'Metal Roofing', lifespan: '40-70 years', bestFor: 'Long-term investment', benefits: 'Durable, energy-efficient, fire-resistant', eco: false },
                { id: 'tile', name: 'Clay/Concrete Tile', lifespan: '50-100 years', bestFor: 'Mediterranean/Spanish homes', benefits: 'Extremely durable, fire-proof, low maintenance', eco: false },
                { id: 'slate', name: 'Natural Slate', lifespan: '75-200 years', bestFor: 'Luxury homes, historic properties', benefits: 'Lifetime durability, elegant appearance, fireproof', eco: false },
                { id: 'recycled_shingles', name: 'Recycled Shingles', lifespan: '20-30 years', bestFor: 'Eco-conscious budgets', benefits: 'Sustainable, affordable, diverts waste', eco: true },
                { id: 'solar_tiles', name: 'Solar Tiles', lifespan: '25-30 years', bestFor: 'Energy independence seekers', benefits: '30% federal tax credit, $1K-$2K/year savings', eco: true },
                { id: 'green_roof', name: 'Green/Living Roof', lifespan: '30-50 years', bestFor: 'Urban sustainability projects', benefits: 'State incentives, 20-30% cooling savings, stormwater management', eco: true },
                { id: 'recycled_metal', name: 'Recycled Metal', lifespan: '40-60 years', bestFor: 'Eco + durability balance', benefits: '100% recyclable, long-lasting, energy-efficient', eco: true },
                { id: 'composite_slate', name: 'Composite Slate', lifespan: '40-60 years', bestFor: 'Slate look without weight', benefits: 'Lighter than slate, recycled content, lower cost', eco: true },
              ].map((material) => {
                const rates = fallbackMaterialRates[material.id]
                const isSelected = state.material === material.id
                return (
                  <tr key={material.id} className={`border-b border-gray-200 hover:bg-blue-50 transition-colors ${
                    isSelected ? 'bg-blue-100 border-l-4 border-l-blue-800' : ''
                  }`}>
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        <span className={`font-semibold ${
                          isSelected ? 'text-blue-800' : 'text-gray-900'
                        }`}>{material.name}</span>
                        {isSelected && (
                          <span className="bg-blue-800 text-white text-xs px-2 py-1 rounded-full">Selected</span>
                        )}
                        {material.eco && (
                          <Leaf size={16} className="text-green-600" aria-label="Eco-friendly" />
                        )}
                      </div>
                    </td>
                    <td className="p-3 font-medium text-gray-700">
                      {currentCountry.currencySymbol}{rates.low.toFixed(2)} - {currentCountry.currencySymbol}{rates.high.toFixed(2)}
                    </td>
                    <td className="p-3 text-gray-600">
                      <div className="flex items-center gap-1">
                        <Clock size={14} className="text-gray-400" aria-hidden="true" />
                        {material.lifespan}
                      </div>
                    </td>
                    <td className="p-3 text-gray-600 text-xs">{material.bestFor}</td>
                    <td className="p-3 text-gray-600 text-xs">{material.benefits}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
        <p className="text-xs text-gray-500 mt-4 text-center">
          <Leaf size={14} className="inline text-green-600 mr-1" aria-hidden="true" />
          = Eco-friendly option with sustainability benefits
        </p>
      </div>

      {/* 30-Year Maintenance Cost Comparison */}
      <MaintenanceComparison 
        currentMaterial={state.material}
        currentCountry={currentCountry}
        squareFootage={state.squareFootage}
      />
    </>
  )
}
